import React, { useMemo } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import RackPanel from './RackPanel';
import { VetoControls } from './exclusions';
import { ZoneNumeral } from './Badges';
import { C } from './tokens';
import {
  RestockV2ExcludedStyle, RestockV2Fixture, RestockV2ListExcludedVariant, RestockV2Move,
  RestockV2Plan, RestockV2ThumbTarget,
} from './types';

interface RackSheetProps {
  fixtureId: string | null;
  plan: RestockV2Plan;
  moves: RestockV2Move[];
  walk: string[];
  doneRows: Record<string, boolean>;
  pendingRows: Record<string, boolean>;
  whyRows: Record<string, boolean>;
  thumbIndex: Record<string, RestockV2ThumbTarget>;
  busy: boolean;
  onClose: () => void;
  onAdopt: () => void;
  onDone: (move: RestockV2Move) => void;
  onUndo: (move: RestockV2Move) => void;
  onSkip: (move: RestockV2Move) => void;
  skipBusy: (move: RestockV2Move) => boolean;
  veto: VetoControls;
  excludedStyles: RestockV2ExcludedStyle[];
  keptOffRail?: RestockV2ListExcludedVariant[];
  onUnkeepRow?: (row: RestockV2ListExcludedVariant) => void;
  onAddBack: (sku: string) => void;
  onWhy: (id: string) => void;
  onImage: (target: RestockV2ThumbTarget) => void;
}

// The phone's rack view: the same RackPanel the desktop shows in its right column, wrapped in a
// full-screen sheet with a back arrow, because on a phone there is no room for two columns.
const RackSheet: React.FC<RackSheetProps> = ({
  fixtureId, plan, moves, walk, doneRows, pendingRows, whyRows, thumbIndex, busy,
  onClose, onAdopt, onDone, onUndo, onSkip, skipBusy, veto, excludedStyles, keptOffRail,
  onUnkeepRow, onAddBack, onWhy, onImage,
}) => {
  const fixture: RestockV2Fixture | null = useMemo(
    () => (plan.racks?.fixtures || []).filter((row) => row.id === fixtureId)[0] || null,
    [plan.racks, fixtureId],
  );

  return (
    <Dialog
      open={!!fixtureId && !!fixture} onClose={onClose} fullScreen
      aria-labelledby="restock-v2-rack-title"
      PaperProps={{ sx: { bgcolor: C.paper } }}
    >
      {fixture && (
        <>
          <Box
            sx={{
              position: 'sticky', top: 0, zIndex: 5, bgcolor: C.ink, color: C.onInk,
              display: 'flex', alignItems: 'center', gap: 1.2, px: 1.5, py: 1.4,
              borderBottom: '2px solid', borderColor: C.back,
            }}
          >
            <Box
              component="button" type="button" onClick={onClose} aria-label="回地圖"
              sx={{
                appearance: 'none', bgcolor: '#302921', border: 0, color: C.onInk, borderRadius: '8px',
                width: 34, height: 34, fontSize: 16, fontWeight: 700, flex: 'none', cursor: 'pointer',
              }}
            >
              ←
            </Box>
            <ZoneNumeral index={walk.indexOf(fixture.id)} bg={C.back} size={11} />
            <Typography
              component="h2" id="restock-v2-rack-title"
              sx={{ fontSize: 16.5, fontWeight: 900, letterSpacing: '-.01em' }}
            >
              {fixture.label}
            </Typography>
          </Box>

          <Box sx={{ px: 1.5, pt: 1.5, pb: 13, maxWidth: 720, mx: 'auto', width: '100%' }}>
            <RackPanel
              fixture={fixture} plan={plan} moves={moves} doneRows={doneRows} pendingRows={pendingRows}
              whyRows={whyRows} thumbIndex={thumbIndex} desktop={false} busy={busy}
              onAdopt={onAdopt} onDone={onDone} onUndo={onUndo} onSkip={onSkip} skipBusy={skipBusy}
              veto={veto} excludedStyles={excludedStyles} keptOffRail={keptOffRail}
              onUnkeepRow={onUnkeepRow} onAddBack={onAddBack} onWhy={onWhy} onImage={onImage}
            />
          </Box>

          <Box
            sx={{
              position: 'sticky', bottom: 0, zIndex: 6, px: 1.5, py: 1.5,
              background: `linear-gradient(to top, ${C.paper} 62%, rgba(244,239,230,0))`,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, maxWidth: 720, mx: 'auto' }}>
              <Box
                component="button" type="button" onClick={onClose} aria-label="回地圖"
                sx={{
                  flex: '0 0 auto', px: 2, minHeight: 48, cursor: 'pointer', borderRadius: '10px',
                  bgcolor: C.card, border: '1px solid', borderColor: C.line2, color: C.ink2,
                  fontWeight: 800, fontSize: 15,
                }}
              >
                回地圖
              </Box>
              <Box
                component="button" type="button" disabled={busy} onClick={onAdopt}
                sx={{
                  flex: 1, minHeight: 48, border: 0, borderRadius: '10px', cursor: 'pointer',
                  bgcolor: C.ink, color: '#FBF6EC', fontWeight: 800, fontSize: 15,
                  '&:disabled': { opacity: 0.55, cursor: 'default' },
                }}
              >
                套用這個版型
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default RackSheet;
